import { useState, useEffect } from "react";
import {
    Users,
    ClipboardList,
    Clock,
    CheckCircle,
    Package,
    PlusCircle,
    Banknote,
    TrendingUp,
    TrendingDown,
    ArrowUpDown,
    ShoppingCart,
    BarChart2,
    Truck,
    RotateCcw,
    XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { dashboardApi } from "../../api/dashboard.js";
import { callAdminAPI } from "../../api/callAdmin.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler,
);

// ── Shimmer CSS (injected once) ───────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("sk-css")) {
    const s = document.createElement("style");
    s.id = "sk-css";
    s.textContent = `@keyframes sk-shimmer{0%,100%{opacity:1}50%{opacity:.45}}@media(max-width:768px){.dash-2col{grid-template-columns:1fr!important}.dash-pa-grid{grid-template-columns:1fr!important}.dash-sum-grid{grid-template-columns:1fr!important}}.breakdown-modal{position:fixed;top:56px;left:var(--sidebar-w);right:0;bottom:0;background:#fff;z-index:60;display:flex;flex-direction:column}@media(max-width:768px){.breakdown-modal{left:0}}`;
    document.head.appendChild(s);
}

// ── Skeleton block ────────────────────────────────────────────────────────────
function Sk({ h = 14, w = "100%", radius = 6, style = {} }) {
    return (
        <div
            style={{
                height: h,
                width: w,
                borderRadius: radius,
                background: "#e5e7eb",
                animation: "sk-shimmer 1.4s ease-in-out infinite",
                flexShrink: 0,
                ...style,
            }}
        />
    );
}

// ── Date helpers for deep-linking stat cards into Orders/Customers ───────────
const pad2 = (n) => String(n).padStart(2, "0");
function toDateStr(d) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function todayStr() {
    return toDateStr(new Date());
}
function fmtRangeDate(str) {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

// ── Persisted date range (survives reload + route changes) ───────────────────
const DASHBOARD_RANGE_KEY = "admin_dashboard_date_range";
function loadStoredRange(defaultStart, defaultEnd) {
    try {
        const raw = localStorage.getItem(DASHBOARD_RANGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.start && parsed?.end) return parsed;
        }
    } catch {
        /* ignore — fall back to defaults */
    }
    return { start: defaultStart, end: defaultEnd };
}
function saveStoredRange(range) {
    try {
        localStorage.setItem(DASHBOARD_RANGE_KEY, JSON.stringify(range));
    } catch {
        /* ignore — storage unavailable/full */
    }
}

// ── Month dropdown options (last 12 months) ───────────────────────────────────
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const y = d.getFullYear(),
        m = String(d.getMonth() + 1).padStart(2, "0");
    return {
        value: `${y}-${m}`,
        label: d.toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric",
        }),
    };
});

// ── Order status config ───────────────────────────────────────────────────────
const STATUS_CONFIG = {
    pending: {
        label: "Pending",
        Icon: Clock,
        color: "#f59e0b",
        bg: "#fffbeb",
        border: "#fde68a",
    },
    accounts_approved: {
        label: "Accounts Approved",
        Icon: CheckCircle,
        color: "#3b82f6",
        bg: "#eff6ff",
        border: "#bfdbfe",
    },
    dispatched: {
        label: "Dispatched",
        Icon: Package,
        color: "#8b5cf6",
        bg: "#f5f3ff",
        border: "#ddd6fe",
    },
    out_for_delivery: {
        label: "Out for Delivery",
        Icon: Truck,
        color: "#06b6d4",
        bg: "#ecfeff",
        border: "#a5f3fc",
    },
    delivered: {
        label: "Delivered",
        Icon: CheckCircle,
        color: "#16a34a",
        bg: "#f0fdf4",
        border: "#bbf7d0",
    },
    hold: {
        label: "On Hold",
        Icon: Clock,
        color: "#6b7280",
        bg: "#f9fafb",
        border: "#e5e7eb",
    },
    returned: {
        label: "Returned",
        Icon: RotateCcw,
        color: "#dc2626",
        bg: "#fef2f2",
        border: "#fecaca",
    },
    cancelled: {
        label: "Cancelled",
        Icon: XCircle,
        color: "#9ca3af",
        bg: "#f9fafb",
        border: "#e5e7eb",
    },
};
const STATUS_ORDER = [
    "pending",
    "accounts_approved",
    "dispatched",
    "out_for_delivery",
    "delivered",
    "hold",
    "returned",
    "cancelled",
];

// ── Orders per day bar chart ──────────────────────────────────────────────────
function BarChart({ data }) {
    const chartData = {
        labels: data.map((d) => d.label),
        datasets: [
            {
                data: data.map((d) => d.value),
                backgroundColor: data.map((_, i) =>
                    i === data.length - 1 ? "#16a34a" : "#bfdbfe",
                ),
                borderRadius: 4,
                borderSkipped: false,
            },
        ],
    };
    return (
        <div style={{ height: 120 }}>
            <Bar
                data={chartData}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.parsed.y} orders`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: { font: { size: 10 }, color: "#9ca3af" },
                        },
                        y: {
                            grid: { color: "#f3f4f6" },
                            ticks: {
                                font: { size: 10 },
                                color: "#9ca3af",
                                stepSize: 1,
                            },
                            beginAtZero: true,
                        },
                    },
                }}
            />
        </div>
    );
}

// ── Order status donut ────────────────────────────────────────────────────────
// Multi-color status breakdown donut. Pass `segments: [{ label, count, color }]`.
function DonutChart({ segments = [], size = 120 }) {
    const withData = segments.filter((s) => s.count > 0);
    const hasData = withData.length > 0;
    const slices = hasData
        ? withData
        : [{ label: "No orders", count: 1, color: "#e2e8f0" }];

    return (
        <Doughnut
            data={{
                labels: slices.map((s) => s.label),
                datasets: [
                    {
                        data: slices.map((s) => s.count),
                        backgroundColor: slices.map((s) => s.color),
                        borderWidth: 0,
                    },
                ],
            }}
            width={size}
            height={size}
            options={{
                cutout: "70%",
                responsive: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: hasData,
                        callbacks: {
                            label: (ctx) => ` ${ctx.label}: ${ctx.parsed}`,
                        },
                    },
                },
            }}
        />
    );
}

// ── Breakdown modal — full-screen pie + full segment list, opened via "View Breakdown" ──
function BreakdownModal({ title, segments, total, onClose, onRowClick }) {
    const sorted = [...segments].sort((a, b) => b.count - a.count);
    return (
        <div className="breakdown-modal">
            <div
                style={{
                    padding: "20px 32px",
                    borderBottom: "1px solid var(--border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                }}
            >
                <div>
                    <div style={{ fontWeight: 800, fontSize: 20 }}>{title}</div>
                    <div
                        style={{
                            fontSize: 13,
                            color: "var(--text-muted)",
                            marginTop: 2,
                        }}
                    >
                        {total} total
                    </div>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid var(--border)",
                        background: "#fff",
                        cursor: "pointer",
                        fontSize: 20,
                        color: "var(--text-muted)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    ×
                </button>
            </div>
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 48,
                    justifyContent: "center",
                    alignItems: "flex-start",
                    padding: "48px 6%",
                }}
            >
                <div style={{ position: "relative", flexShrink: 0 }}>
                    <DonutChart segments={segments} size={380} />
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%,-50%)",
                            textAlign: "center",
                        }}
                    >
                        <div style={{ fontWeight: 800, fontSize: 40 }}>
                            {total}
                        </div>
                        <div
                            style={{ fontSize: 13, color: "var(--text-muted)" }}
                        >
                            Total
                        </div>
                    </div>
                </div>
                <div
                    style={{ flex: "1 1 380px", maxWidth: 560, minWidth: 300 }}
                >
                    {sorted.map((r) => (
                        <div
                            key={r.label}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "13px 16px",
                                borderBottom: "1px solid #f1f5f9",
                                borderRadius: 8,
                                cursor: onRowClick ? "pointer" : "default",
                            }}
                            onClick={() => onRowClick && onRowClick(r)}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.background = onRowClick
                                    ? "#f8fafc"
                                    : "")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "")
                            }
                        >
                            <div
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 3,
                                    background: r.color,
                                    flexShrink: 0,
                                }}
                            />
                            <div
                                style={{
                                    flex: 1,
                                    fontSize: 14,
                                    fontWeight: 600,
                                }}
                            >
                                {r.label}
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>
                                {r.count}
                            </div>
                            <span
                                className="badge badge-gray"
                                style={{
                                    fontSize: 11,
                                    minWidth: 40,
                                    textAlign: "center",
                                }}
                            >
                                {total
                                    ? Math.round((r.count / total) * 100)
                                    : 0}
                                %
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Revenue / Cost / Profit donut ─────────────────────────────────────────────
function BreakdownDonut({ revenue, cost, profit }) {
    const COLORS = ["#3b82f6", "#f59e0b", "#16a34a"];
    const BG = ["#eff6ff", "#fffbeb", "#f0fdf4"];
    const LABELS = ["Revenue", "Cost", "Profit"];
    const VALUES = [revenue, cost, profit];
    const total = revenue || 1;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
                style={{
                    position: "relative",
                    flexShrink: 0,
                    width: 160,
                    height: 160,
                }}
            >
                <Doughnut
                    data={{
                        labels: LABELS,
                        datasets: [
                            {
                                data: VALUES,
                                backgroundColor: COLORS,
                                borderWidth: 0,
                                hoverOffset: 4,
                            },
                        ],
                    }}
                    options={{
                        cutout: "62%",
                        responsive: false,
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) =>
                                        ` ₹${Math.round(ctx.parsed).toLocaleString("en-IN")} (${Math.round((ctx.parsed / total) * 100)}%)`,
                                },
                            },
                        },
                    }}
                    width={160}
                    height={160}
                />
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        textAlign: "center",
                        pointerEvents: "none",
                    }}
                >
                    <div
                        style={{
                            fontSize: 9,
                            color: "#9ca3af",
                            fontWeight: 600,
                            lineHeight: 1.3,
                        }}
                    >
                        Revenue
                        <br />
                        Breakdown
                    </div>
                </div>
            </div>
            <div style={{ flex: 1 }}>
                {LABELS.map((label, i) => {
                    const pct = Math.round((VALUES[i] / total) * 100);
                    return (
                        <div key={label} style={{ marginBottom: 12 }}>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginBottom: 3,
                                }}
                            >
                                <div
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: 2,
                                        background: COLORS[i],
                                        flexShrink: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#374151",
                                    }}
                                >
                                    {label}
                                </span>
                                <span
                                    style={{
                                        marginLeft: "auto",
                                        fontSize: 11,
                                        fontWeight: 700,
                                        background: BG[i],
                                        color: COLORS[i],
                                        padding: "1px 6px",
                                        borderRadius: 4,
                                    }}
                                >
                                    {pct}%
                                </span>
                            </div>
                            <div
                                style={{
                                    height: 5,
                                    background: "#f3f4f6",
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    marginLeft: 16,
                                }}
                            >
                                <div
                                    style={{
                                        height: "100%",
                                        width: `${pct}%`,
                                        background: COLORS[i],
                                        borderRadius: 3,
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 11,
                                    color: "#6b7280",
                                    marginLeft: 16,
                                    marginTop: 2,
                                }}
                            >
                                ₹{Math.round(VALUES[i]).toLocaleString("en-IN")}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Top profitable products (horizontal bar) ──────────────────────────────────
function TopProfitBars({ products }) {
    if (!products.length)
        return (
            <div
                style={{
                    color: "#9ca3af",
                    fontSize: 12,
                    textAlign: "center",
                    padding: 24,
                }}
            >
                No product data
            </div>
        );
    const fmtX = (v) =>
        v >= 100000
            ? `₹${(v / 100000).toFixed(1)}L`
            : v >= 1000
              ? `₹${(v / 1000).toFixed(0)}k`
              : `₹${v}`;
    return (
        <div style={{ height: Math.max(180, products.length * 44) }}>
            <Bar
                data={{
                    labels: products.map((p) =>
                        p.productName.length > 22
                            ? p.productName.slice(0, 20) + "…"
                            : p.productName,
                    ),
                    datasets: [
                        {
                            data: products.map((p) => p.profit),
                            backgroundColor: "rgba(22,163,74,0.75)",
                            hoverBackgroundColor: "#16a34a",
                            borderRadius: 4,
                            borderSkipped: false,
                        },
                    ],
                }}
                options={{
                    indexAxis: "y",
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) =>
                                    ` ₹${Math.round(ctx.parsed.x).toLocaleString("en-IN")} profit`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: { color: "#f3f4f6" },
                            ticks: {
                                font: { size: 9 },
                                color: "#9ca3af",
                                callback: fmtX,
                            },
                            beginAtZero: true,
                        },
                        y: {
                            grid: { display: false },
                            ticks: { font: { size: 11 }, color: "#374151" },
                        },
                    },
                }}
            />
        </div>
    );
}

// ── Shared currency formatting for chart axes/tooltips ────────────────────────
const fmtCompactMoney = (v) =>
    Math.abs(v) >= 100000
        ? `₹${(v / 100000).toFixed(1)}L`
        : Math.abs(v) >= 1000
          ? `₹${(v / 1000).toFixed(0)}k`
          : `₹${Math.round(v)}`;

const hexToRgba = (hex, alpha) => {
    const h = hex.replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

const MONTH_LABEL_FMT = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-IN", {
        month: "short",
        year: "numeric",
    });
};

// ── Revenue trend — full-width line chart (trailing 12 months) ───────────────
function RevenueTrendChart({ data }) {
    if (!data.length)
        return (
            <div
                style={{
                    padding: 40,
                    textAlign: "center",
                    color: "var(--text-muted)",
                    fontSize: 13,
                }}
            >
                No revenue data yet
            </div>
        );
    const labels = data.map((d) => MONTH_LABEL_FMT(d.month));
    const values = data.map((d) => d.revenue);
    return (
        <div style={{ height: 280 }}>
            <Line
                data={{
                    labels,
                    datasets: [
                        {
                            label: "Revenue",
                            data: values,
                            borderColor: "#16a34a",
                            backgroundColor: hexToRgba("#16a34a", 0.12),
                            fill: true,
                            tension: 0.35,
                            pointRadius: 3,
                            pointHoverRadius: 6,
                            pointBackgroundColor: "#16a34a",
                            borderWidth: 2,
                        },
                    ],
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) =>
                                    ` ₹${Math.round(ctx.parsed.y).toLocaleString("en-IN")}`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: { font: { size: 11 }, color: "#9ca3af" },
                        },
                        y: {
                            grid: { color: "#f3f4f6" },
                            ticks: {
                                font: { size: 11 },
                                color: "#9ca3af",
                                callback: fmtCompactMoney,
                            },
                            beginAtZero: true,
                        },
                    },
                }}
            />
        </div>
    );
}

// ── Generic top-N ranking line chart — replaces plain tables in Profitability ──
function RankingBarChart({
    rows,
    labelKey,
    valueKey,
    lineColor = "#16a34a",
    tooltipTitle,
    tooltipLabel,
    onPointClick,
    emptyText = "No data in this range",
    height = 260,
    formatY = fmtCompactMoney,
}) {
    if (!rows.length)
        return (
            <div
                style={{
                    padding: 24,
                    textAlign: "center",
                    color: "var(--text-muted)",
                    fontSize: 13,
                }}
            >
                {emptyText}
            </div>
        );
    // Always plot lowest → highest left to right, regardless of which sort
    // order was used to pick this top/bottom-N set.
    const chartRows = [...rows].sort((a, b) => a[valueKey] - b[valueKey]);
    const labels = chartRows.map((r) => {
        const s = String(r[labelKey] ?? "—");
        return s.length > 16 ? s.slice(0, 14) + "…" : s;
    });
    const values = chartRows.map((r) => r[valueKey]);
    // Fewer bars get more breathing room per bar (up to a point); more bars shrink the
    // gap between them so each one stays wide enough to see and hover over reliably.
    const barCount = chartRows.length;
    const categoryPercentage = barCount <= 4 ? 0.5 : barCount <= 8 ? 0.75 : 0.9;
    const barPercentage = barCount <= 4 ? 0.6 : barCount <= 8 ? 0.85 : 0.95;
    const maxBarThickness = barCount <= 4 ? 90 : barCount <= 8 ? 64 : 44;
    return (
        <div style={{ height, padding: "12px 8px 0" }}>
            <Bar
                data={{
                    labels,
                    datasets: [
                        {
                            data: values,
                            backgroundColor: hexToRgba(lineColor, 0.75),
                            hoverBackgroundColor: lineColor,
                            borderRadius: 4,
                            borderSkipped: false,
                            categoryPercentage,
                            barPercentage,
                            maxBarThickness,
                            // Without this, a handful of large outliers (e.g. one big account)
                            // squash every smaller bar down to a sliver that's nearly
                            // impossible to see or hover — force a visible floor height instead.
                            minBarLength: 8,
                        },
                    ],
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    // Trigger on the whole category column, not just the (possibly thin) bar
                    // itself, so hovering/clicking near a bar still hits it reliably.
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: (items) =>
                                    tooltipTitle
                                        ? tooltipTitle(
                                              chartRows[items[0].dataIndex],
                                          )
                                        : String(
                                              chartRows[items[0].dataIndex][
                                                  labelKey
                                              ],
                                          ),
                                label: (ctx) =>
                                    tooltipLabel(chartRows[ctx.dataIndex]),
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: {
                                font: { size: 10 },
                                color: "#9ca3af",
                                maxRotation: 40,
                                minRotation: 0,
                                autoSkip: true,
                            },
                        },
                        y: {
                            grid: { color: "#f3f4f6" },
                            ticks: {
                                font: { size: 10 },
                                color: "#9ca3af",
                                callback: formatY,
                            },
                            beginAtZero: true,
                        },
                    },
                    onClick: (_evt, elements) => {
                        if (elements.length && onPointClick) {
                            onPointClick(chartRows[elements[0].index]);
                        }
                    },
                    onHover: (evt, elements) => {
                        if (onPointClick && evt.native) {
                            evt.native.target.style.cursor = elements.length
                                ? "pointer"
                                : "default";
                        }
                    },
                }}
            />
        </div>
    );
}

// ── Skeleton for stat cards ───────────────────────────────────────────────────
function StatCardsSkeleton() {
    return (
        <div
            className="stats-grid"
            style={{
                gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
            }}
        >
            {Array(5)
                .fill(0)
                .map((_, i) => (
                    <div key={i} className="stat-card" style={{ gap: 10 }}>
                        <Sk h={12} w="60%" />
                        <Sk h={28} w="50%" style={{ marginTop: 4 }} />
                        <Sk h={10} w="70%" style={{ marginTop: 4 }} />
                    </div>
                ))}
        </div>
    );
}

// ── Skeleton for list rows ────────────────────────────────────────────────────
function ListRowsSkeleton({ rows = 4 }) {
    return Array(rows)
        .fill(0)
        .map((_, i) => (
            <div
                key={i}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 20px",
                    borderBottom: "1px solid #f1f5f9",
                }}
            >
                <Sk h={36} w={36} radius={18} style={{ flexShrink: 0 }} />
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                    }}
                >
                    <Sk h={12} w="55%" />
                    <Sk h={10} w="30%" />
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        alignItems: "flex-end",
                    }}
                >
                    <Sk h={12} w={60} />
                    <Sk h={10} w={48} />
                </div>
            </div>
        ));
}

// ── Skeleton for chart area ───────────────────────────────────────────────────
function ChartSkeleton({ height = 120 }) {
    return (
        <div
            style={{
                height,
                display: "flex",
                alignItems: "flex-end",
                gap: 6,
                padding: "4px 0",
            }}
        >
            {Array(12)
                .fill(0)
                .map((_, i) => (
                    <Sk
                        key={i}
                        w="100%"
                        h={`${30 + Math.random() * 60}%`}
                        style={{ alignSelf: "flex-end" }}
                    />
                ))}
        </div>
    );
}

// ── Skeleton for product table rows ──────────────────────────────────────────
function TableRowsSkeleton({ rows = 5, cols = 7 }) {
    return Array(rows)
        .fill(0)
        .map((_, i) => (
            <tr key={i}>
                {Array(cols)
                    .fill(0)
                    .map((__, j) => (
                        <td key={j} style={{ padding: "11px 14px" }}>
                            <Sk h={12} w={j === 1 ? "80%" : "60%"} />
                        </td>
                    ))}
            </tr>
        ));
}

// ── Section divider ───────────────────────────────────────────────────────────
function SectionDivider({ title }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                margin: "28px 0 16px",
            }}
        >
            <div
                style={{
                    width: 4,
                    height: 20,
                    background: "var(--primary)",
                    borderRadius: 2,
                    flexShrink: 0,
                }}
            />
            <span
                style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--text)",
                    whiteSpace: "nowrap",
                }}
            >
                {title}
            </span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        </div>
    );
}

// ── Profitability section ───────────────────────────────────────────────────────
const PROFIT_DATE_INPUT_STYLE = {
    fontSize: 12,
    border: "1px solid var(--border)",
    borderRadius: 7,
    padding: "5px 8px",
    color: "var(--text)",
    outline: "none",
    cursor: "pointer",
};

function SortToggleButton({ sortOrder, onClick }) {
    return (
        <button
            onClick={onClick}
            title={
                sortOrder === "desc"
                    ? "Show least profitable / lowest first"
                    : "Show most profitable / highest first"
            }
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: 20,
                border: "1px solid var(--border)",
                background: "#fff",
                color: "var(--text-muted)",
                cursor: "pointer",
                whiteSpace: "nowrap",
            }}
        >
            <ArrowUpDown size={11} />
            {sortOrder === "desc" ? "Highest first" : "Lowest first"}
        </button>
    );
}

const LIMIT_OPTIONS = [10, 25, 50];

function LimitSelect({ limit, onChange }) {
    return (
        <select
            value={limit}
            onChange={(e) => onChange(Number(e.target.value))}
            title="Rows to show"
            style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 8px",
                borderRadius: 7,
                border: "1px solid var(--border)",
                background: "#fff",
                color: "var(--text-muted)",
                cursor: "pointer",
                outline: "none",
            }}
        >
            {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                    Top {n}
                </option>
            ))}
        </select>
    );
}

function ProfitabilitySection({ startDate, endDate }) {
    const navigate = useNavigate();

    const [ordersSort, setOrdersSort] = useState("desc");
    const [revenueSort, setRevenueSort] = useState("desc");
    const [profitSort, setProfitSort] = useState("desc");
    const [productsSort, setProductsSort] = useState("desc");
    const [salesPersonSort, setSalesPersonSort] = useState("desc");

    const [ordersLimit, setOrdersLimit] = useState(10);
    const [revenueLimit, setRevenueLimit] = useState(10);
    const [profitLimit, setProfitLimit] = useState(10);
    const [productsLimit, setProductsLimit] = useState(10);
    const [salesPersonLimit, setSalesPersonLimit] = useState(10);

    const [ordersLoading, setOrdersLoading] = useState(true);
    const [revenueLoading, setRevenueLoading] = useState(true);
    const [profitLoading, setProfitLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [salesPersonLoading, setSalesPersonLoading] = useState(true);

    const [orders, setOrders] = useState([]);
    const [revenueCustomers, setRevenueCustomers] = useState([]);
    const [profitCustomers, setProfitCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [salesPersons, setSalesPersons] = useState([]);

    useEffect(() => {
        setOrdersLoading(true);
        dashboardApi
            .getOrderProfitability(
                startDate,
                endDate,
                1,
                ordersLimit,
                "profit",
                ordersSort,
            )
            .then((o) => setOrders(o?.data?.orders ?? []))
            .catch(() => setOrders([]))
            .finally(() => setOrdersLoading(false));
    }, [startDate, endDate, ordersSort, ordersLimit]);

    useEffect(() => {
        setRevenueLoading(true);
        dashboardApi
            .getTopRevenueCustomers(
                startDate,
                endDate,
                1,
                revenueLimit,
                revenueSort,
            )
            .then((rc) => setRevenueCustomers(rc?.data?.customers ?? []))
            .catch(() => setRevenueCustomers([]))
            .finally(() => setRevenueLoading(false));
    }, [startDate, endDate, revenueSort, revenueLimit]);

    useEffect(() => {
        setProfitLoading(true);
        dashboardApi
            .getTopProfitableCustomers(
                startDate,
                endDate,
                1,
                profitLimit,
                profitSort,
            )
            .then((pc) => setProfitCustomers(pc?.data?.customers ?? []))
            .catch(() => setProfitCustomers([]))
            .finally(() => setProfitLoading(false));
    }, [startDate, endDate, profitSort, profitLimit]);

    useEffect(() => {
        setProductsLoading(true);
        dashboardApi
            .getProductProfitability(
                startDate,
                endDate,
                1,
                productsLimit,
                productsSort,
            )
            .then((p) => setProducts(p?.data?.products ?? []))
            .catch(() => setProducts([]))
            .finally(() => setProductsLoading(false));
    }, [startDate, endDate, productsSort, productsLimit]);

    useEffect(() => {
        setSalesPersonLoading(true);
        dashboardApi
            .getTopRevenueSalesPersons(
                startDate,
                endDate,
                1,
                salesPersonLimit,
                salesPersonSort,
            )
            .then((sp) => setSalesPersons(sp?.data?.salesPersons ?? []))
            .catch(() => setSalesPersons([]))
            .finally(() => setSalesPersonLoading(false));
    }, [startDate, endDate, salesPersonSort, salesPersonLimit]);

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    margin: "28px 0 16px",
                    flexWrap: "wrap",
                }}
            >
                <div
                    style={{
                        width: 4,
                        height: 20,
                        background: "var(--primary)",
                        borderRadius: 2,
                        flexShrink: 0,
                    }}
                />
                <span
                    style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--text)",
                        whiteSpace: "nowrap",
                    }}
                >
                    Profitability
                </span>
                <div
                    style={{
                        flex: 1,
                        height: 1,
                        background: "var(--border)",
                        minWidth: 20,
                    }}
                />
            </div>

            <div
                className="dash-pa-grid"
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                }}
            >
                {/* Per-order profitability */}
                <div className="card">
                    <div className="card-header">
                        <span
                            className="card-title"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                            }}
                        >
                            {ordersSort === "desc" ? (
                                <TrendingUp size={15} />
                            ) : (
                                <TrendingDown size={15} />
                            )}
                            {ordersSort === "desc"
                                ? "Most Profitable Orders"
                                : "Least Profitable Orders"}
                        </span>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <LimitSelect
                                limit={ordersLimit}
                                onChange={setOrdersLimit}
                            />
                            <SortToggleButton
                                sortOrder={ordersSort}
                                onClick={() =>
                                    setOrdersSort((s) =>
                                        s === "desc" ? "asc" : "desc",
                                    )
                                }
                            />
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={() =>
                                    navigate(
                                        `/orders?status=delivered&date_from=${startDate}&date_to=${endDate}`,
                                    )
                                }
                            >
                                View All
                            </button>
                        </div>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {ordersLoading ? (
                            <ListRowsSkeleton rows={5} />
                        ) : (
                            <RankingBarChart
                                rows={orders}
                                labelKey="leadName"
                                valueKey="profit"
                                lineColor={
                                    ordersSort === "desc"
                                        ? "#16a34a"
                                        : "#dc2626"
                                }
                                emptyText="No orders in this range"
                                tooltipTitle={(r) =>
                                    `#${r.orderId} — ${r.leadName}`
                                }
                                tooltipLabel={(r) =>
                                    ` Profit: ${r.profit >= 0 ? "+" : ""}${fmt(r.profit)} (${r.margin}%) · Revenue ${fmt(r.revenue)}`
                                }
                                onPointClick={(r) =>
                                    navigate(`/orders/${r.orderId}`)
                                }
                            />
                        )}
                    </div>
                </div>

                {/* Top revenue customers */}
                <div className="card">
                    <div className="card-header">
                        <span
                            className="card-title"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                            }}
                        >
                            <Banknote size={15} />{" "}
                            {revenueSort === "desc"
                                ? "Top Revenue Customers"
                                : "Lowest Revenue Customers"}
                        </span>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11,
                                    color: "var(--text-muted)",
                                }}
                            >
                                Gross order value
                            </span>
                            <LimitSelect
                                limit={revenueLimit}
                                onChange={setRevenueLimit}
                            />
                            <SortToggleButton
                                sortOrder={revenueSort}
                                onClick={() =>
                                    setRevenueSort((s) =>
                                        s === "desc" ? "asc" : "desc",
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {revenueLoading ? (
                            <ListRowsSkeleton rows={5} />
                        ) : (
                            <RankingBarChart
                                rows={revenueCustomers}
                                labelKey="leadName"
                                valueKey="totalRevenue"
                                lineColor="#3b82f6"
                                emptyText="No customer activity in this range"
                                tooltipTitle={(r) => r.leadName}
                                tooltipLabel={(r) =>
                                    ` Revenue: ${fmt(r.totalRevenue)} · ${r.orderCount} order${r.orderCount !== 1 ? "s" : ""}`
                                }
                                onPointClick={(r) =>
                                    navigate(`/customers/${r.leadId}`)
                                }
                            />
                        )}
                    </div>
                </div>

                {/* Top profitable clients */}
                <div className="card">
                    <div className="card-header">
                        <span
                            className="card-title"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                            }}
                        >
                            {profitSort === "desc" ? (
                                <TrendingUp size={15} />
                            ) : (
                                <TrendingDown size={15} />
                            )}
                            {profitSort === "desc"
                                ? "Top Profitable Clients"
                                : "Least Profitable Clients"}
                        </span>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11,
                                    color: "var(--text-muted)",
                                }}
                            >
                                Revenue − making cost
                            </span>
                            <LimitSelect
                                limit={profitLimit}
                                onChange={setProfitLimit}
                            />
                            <SortToggleButton
                                sortOrder={profitSort}
                                onClick={() =>
                                    setProfitSort((s) =>
                                        s === "desc" ? "asc" : "desc",
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {profitLoading ? (
                            <ListRowsSkeleton rows={5} />
                        ) : (
                            <RankingBarChart
                                rows={profitCustomers}
                                labelKey="leadName"
                                valueKey="totalProfit"
                                lineColor={
                                    profitSort === "desc"
                                        ? "#0891b2"
                                        : "#dc2626"
                                }
                                emptyText="No customer activity in this range"
                                tooltipTitle={(r) => r.leadName}
                                tooltipLabel={(r) =>
                                    ` Profit: ${r.totalProfit >= 0 ? "+" : ""}${fmt(r.totalProfit)} (${r.margin}%) · Revenue ${fmt(r.totalRevenue)}`
                                }
                                onPointClick={(r) =>
                                    navigate(`/customers/${r.leadId}`)
                                }
                            />
                        )}
                    </div>
                </div>

                {/* Per-product profitability */}
                <div className="card">
                    <div className="card-header">
                        <span
                            className="card-title"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                            }}
                        >
                            <Package size={15} />{" "}
                            {productsSort === "desc"
                                ? "Top Profitable Products"
                                : "Least Profitable Products"}
                        </span>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11,
                                    color: "var(--text-muted)",
                                }}
                            >
                                Revenue - making cost
                            </span>
                            <LimitSelect
                                limit={productsLimit}
                                onChange={setProductsLimit}
                            />
                            <SortToggleButton
                                sortOrder={productsSort}
                                onClick={() =>
                                    setProductsSort((s) =>
                                        s === "desc" ? "asc" : "desc",
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {productsLoading ? (
                            <ListRowsSkeleton rows={5} />
                        ) : (
                            <RankingBarChart
                                rows={products}
                                labelKey="productName"
                                valueKey="profit"
                                lineColor={
                                    productsSort === "desc"
                                        ? "#8b5cf6"
                                        : "#dc2626"
                                }
                                emptyText="No product sales in this range"
                                tooltipTitle={(r) => r.productName}
                                tooltipLabel={(r) =>
                                    ` Profit: ${r.profit >= 0 ? "+" : ""}${fmt(r.profit)} (${r.margin}%) · Qty ${r.qtySold} · Revenue ${fmt(r.revenue)}`
                                }
                                onPointClick={(r) =>
                                    navigate(
                                        `/products?search=${encodeURIComponent(r.productName)}`,
                                    )
                                }
                            />
                        )}
                    </div>
                </div>

                {/* Top revenue sales persons */}
                <div className="card">
                    <div className="card-header">
                        <span
                            className="card-title"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                            }}
                        >
                            <Banknote size={15} />{" "}
                            {salesPersonSort === "desc"
                                ? "Top Revenue Sales Persons"
                                : "Lowest Revenue Sales Persons"}
                        </span>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11,
                                    color: "var(--text-muted)",
                                }}
                            >
                                By sales_executive
                            </span>
                            <LimitSelect
                                limit={salesPersonLimit}
                                onChange={setSalesPersonLimit}
                            />
                            <SortToggleButton
                                sortOrder={salesPersonSort}
                                onClick={() =>
                                    setSalesPersonSort((s) =>
                                        s === "desc" ? "asc" : "desc",
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {salesPersonLoading ? (
                            <ListRowsSkeleton rows={5} />
                        ) : (
                            <RankingBarChart
                                rows={salesPersons}
                                labelKey="salesExecutive"
                                valueKey="totalRevenue"
                                lineColor={
                                    salesPersonSort === "desc"
                                        ? "#f59e0b"
                                        : "#dc2626"
                                }
                                emptyText="No sales activity in this range"
                                tooltipTitle={(r) => r.salesExecutive}
                                tooltipLabel={(r) =>
                                    ` Revenue: ${fmt(r.totalRevenue)} · ${r.orderCount} order${r.orderCount !== 1 ? "s" : ""}`
                                }
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Product Analytics section ─────────────────────────────────────────────────
function ProductAnalyticsDashboard({ onNavigate }) {
    // Independent of the top dashboard's manual date range — this section keeps its own
    // calendar-month picker since product analytics is reported monthly.
    const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].value);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        const [year, month] = selectedMonth.split("-").map(Number);
        setLoading(true);
        dashboardApi
            .getAdminDashboard({ month, year })
            .then((res) => setAnalytics(res.data?.productAnalytics ?? null))
            .catch(() => setAnalytics(null))
            .finally(() => setLoading(false));
    }, [selectedMonth]);

    const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
    const pa = analytics;

    const KPI_CARDS = pa
        ? [
              {
                  label: "Product Revenue",
                  value: fmt(pa.totalRevenue),
                  Icon: Banknote,
                  color: "#3b82f6",
                  bg: "#eff6ff",
                  border: "#bfdbfe",
                  note: "This Month",
              },
              {
                  label: "Estimated Cost",
                  value: fmt(pa.totalCost),
                  Icon: Package,
                  color: "#f59e0b",
                  bg: "#fffbeb",
                  border: "#fde68a",
                  note: "This Month",
              },
              {
                  label: "Total Profit",
                  value: fmt(pa.totalProfit),
                  Icon: TrendingUp,
                  color: "#16a34a",
                  bg: "#f0fdf4",
                  border: "#bbf7d0",
                  note: `${pa.overallMargin}% margin`,
              },
              {
                  label: "Items Sold",
                  value: pa.totalItemsSold.toLocaleString("en-IN"),
                  Icon: ShoppingCart,
                  color: "#8b5cf6",
                  bg: "#f5f3ff",
                  border: "#ddd6fe",
                  note: "Units this month",
              },
          ]
        : [];

    return (
        <div>
            {/* Section header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    margin: "32px 0 18px",
                }}
            >
                <div
                    style={{
                        width: 4,
                        height: 22,
                        background: "var(--primary)",
                        borderRadius: 2,
                        flexShrink: 0,
                    }}
                />
                <span
                    style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "var(--text)",
                        whiteSpace: "nowrap",
                    }}
                >
                    Product Analytics
                </span>
                <div
                    style={{ flex: 1, height: 1, background: "var(--border)" }}
                />
                <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    style={{
                        fontSize: 12.5,
                        border: "1px solid var(--border)",
                        borderRadius: 7,
                        padding: "5px 10px",
                        background: "#fff",
                        color: "var(--text)",
                        fontWeight: 600,
                        cursor: "pointer",
                        outline: "none",
                        flexShrink: 0,
                    }}
                >
                    {MONTH_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* KPI 2×2 + Donut */}
            <div
                className="dash-pa-grid"
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                    }}
                >
                    {loading
                        ? Array(4)
                              .fill(0)
                              .map((_, i) => (
                                  <div
                                      key={i}
                                      className="stat-card"
                                      style={{ gap: 10 }}
                                  >
                                      <Sk h={12} w="65%" />
                                      <Sk
                                          h={28}
                                          w="50%"
                                          style={{ marginTop: 6 }}
                                      />
                                      <Sk
                                          h={10}
                                          w="55%"
                                          style={{ marginTop: 4 }}
                                      />
                                  </div>
                              ))
                        : KPI_CARDS.map((card) => (
                              <div
                                  key={card.label}
                                  className="stat-card"
                                  style={{
                                      borderTop: `3px solid ${card.color}`,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0,
                                  }}
                              >
                                  <div
                                      style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 10,
                                          marginBottom: 10,
                                      }}
                                  >
                                      <div
                                          style={{
                                              width: 36,
                                              height: 36,
                                              borderRadius: 10,
                                              background: card.bg,
                                              border: `1px solid ${card.border}`,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                          }}
                                      >
                                          <card.Icon
                                              size={17}
                                              color={card.color}
                                              strokeWidth={2}
                                          />
                                      </div>
                                      <div
                                          style={{
                                              fontSize: 11,
                                              color: "var(--text-muted)",
                                              fontWeight: 500,
                                              lineHeight: 1.3,
                                          }}
                                      >
                                          {card.label}
                                      </div>
                                  </div>
                                  <div
                                      style={{
                                          fontSize: 22,
                                          fontWeight: 800,
                                          color: card.color,
                                          lineHeight: 1,
                                          marginBottom: 5,
                                      }}
                                  >
                                      {card.value}
                                  </div>
                                  <div
                                      style={{
                                          fontSize: 11,
                                          color: "var(--primary)",
                                          fontWeight: 600,
                                      }}
                                  >
                                      {card.note}
                                  </div>
                              </div>
                          ))}
                </div>

                <div className="card">
                    <div className="card-header">
                        <span className="card-title">
                            Revenue, Cost & Profit Breakdown
                        </span>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div
                                style={{
                                    display: "flex",
                                    gap: 16,
                                    alignItems: "center",
                                }}
                            >
                                <Sk
                                    h={160}
                                    w={160}
                                    radius={80}
                                    style={{ flexShrink: 0 }}
                                />
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 14,
                                    }}
                                >
                                    {[70, 55, 60].map((w, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4,
                                            }}
                                        >
                                            <Sk h={10} w={`${w}%`} />
                                            <Sk h={5} />
                                            <Sk h={9} w="40%" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            pa && (
                                <BreakdownDonut
                                    revenue={pa.totalRevenue}
                                    cost={pa.totalCost}
                                    profit={pa.totalProfit}
                                />
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* Product Profit Summary Table */}
            <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-header">
                    <span className="card-title">Product Profit Summary</span>
                    {!loading && pa && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 12,
                                    color: "var(--text-muted)",
                                }}
                            >
                                {pa.products.length} products
                            </span>
                            {pa.products.length > 10 && (
                                <button
                                    onClick={() => setShowAll((v) => !v)}
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        padding: "4px 12px",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        border: "1.5px solid var(--primary)",
                                        background: showAll
                                            ? "var(--primary)"
                                            : "#fff",
                                        color: showAll
                                            ? "#fff"
                                            : "var(--primary)",
                                    }}
                                >
                                    {showAll
                                        ? "Show Less"
                                        : "View All Products"}
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                    <div style={{ overflowX: "auto" }}>
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: 13,
                            }}
                        >
                            <thead>
                                <tr
                                    style={{
                                        background: "#f8fafc",
                                        borderBottom: "2px solid var(--border)",
                                    }}
                                >
                                    {[
                                        "#",
                                        "Product Name",
                                        "Sold Qty",
                                        "Sales Amount",
                                        "Est. Cost",
                                        "Profit",
                                        "Margin %",
                                    ].map((h) => (
                                        <th
                                            key={h}
                                            style={{
                                                padding: "10px 14px",
                                                textAlign: [
                                                    "#",
                                                    "Sold Qty",
                                                    "Margin %",
                                                ].includes(h)
                                                    ? "center"
                                                    : h === "Product Name"
                                                      ? "left"
                                                      : "right",
                                                fontWeight: 600,
                                                fontSize: "0.72rem",
                                                color: "#6b7280",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.04em",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <TableRowsSkeleton rows={5} cols={7} />
                                ) : !pa || pa.products.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{
                                                padding: 24,
                                                textAlign: "center",
                                                color: "var(--text-muted)",
                                                fontSize: 13,
                                            }}
                                        >
                                            No product data for this period
                                        </td>
                                    </tr>
                                ) : (
                                    (showAll
                                        ? pa.products
                                        : pa.products.slice(0, 10)
                                    ).map((p, i) => (
                                        <tr
                                            key={p.productId}
                                            style={{
                                                background:
                                                    i % 2 === 0
                                                        ? "#fff"
                                                        : "#fafafa",
                                                borderBottom:
                                                    "1px solid #f1f5f9",
                                            }}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.background =
                                                    "#f0fdf4")
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.background =
                                                    i % 2 === 0
                                                        ? "#fff"
                                                        : "#fafafa")
                                            }
                                        >
                                            <td
                                                style={{
                                                    padding: "11px 14px",
                                                    textAlign: "center",
                                                    color: "var(--text-muted)",
                                                    fontSize: 12,
                                                }}
                                            >
                                                {i + 1}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "11px 14px",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {p.productName}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "11px 14px",
                                                    textAlign: "center",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {p.soldQty}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "11px 14px",
                                                    textAlign: "right",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                ₹
                                                {p.revenue.toLocaleString(
                                                    "en-IN",
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "11px 14px",
                                                    textAlign: "right",
                                                    color: "#6b7280",
                                                }}
                                            >
                                                ₹
                                                {p.estimatedCost.toLocaleString(
                                                    "en-IN",
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "11px 14px",
                                                    textAlign: "right",
                                                    fontWeight: 700,
                                                    color:
                                                        p.profit >= 0
                                                            ? "#16a34a"
                                                            : "#dc2626",
                                                }}
                                            >
                                                {p.profit >= 0 ? "+" : ""}₹
                                                {p.profit.toLocaleString(
                                                    "en-IN",
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "11px 14px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        padding: "2px 8px",
                                                        borderRadius: 4,
                                                        fontSize: "0.73rem",
                                                        fontWeight: 700,
                                                        background:
                                                            p.margin >= 30
                                                                ? "#dcfce7"
                                                                : p.margin >= 10
                                                                  ? "#fef3c7"
                                                                  : "#fee2e2",
                                                        color:
                                                            p.margin >= 30
                                                                ? "#16a34a"
                                                                : p.margin >= 10
                                                                  ? "#d97706"
                                                                  : "#dc2626",
                                                    }}
                                                >
                                                    {p.margin.toFixed(1)}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            {!loading && pa && pa.products.length > 0 && (
                                <tfoot>
                                    <tr
                                        style={{
                                            background: "#f8fafc",
                                            borderTop:
                                                "2px solid var(--border)",
                                        }}
                                    >
                                        <td
                                            colSpan={2}
                                            style={{
                                                padding: "10px 14px",
                                                fontWeight: 700,
                                            }}
                                        >
                                            Total
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 14px",
                                                textAlign: "center",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {pa.totalItemsSold}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 14px",
                                                textAlign: "right",
                                                fontWeight: 700,
                                            }}
                                        >
                                            ₹
                                            {pa.totalRevenue.toLocaleString(
                                                "en-IN",
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 14px",
                                                textAlign: "right",
                                                fontWeight: 700,
                                                color: "#6b7280",
                                            }}
                                        >
                                            ₹
                                            {pa.totalCost.toLocaleString(
                                                "en-IN",
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 14px",
                                                textAlign: "right",
                                                fontWeight: 700,
                                                color: "#16a34a",
                                            }}
                                        >
                                            ₹
                                            {pa.totalProfit.toLocaleString(
                                                "en-IN",
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 14px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: "0.75rem",
                                                    fontWeight: 700,
                                                    color: "#16a34a",
                                                }}
                                            >
                                                {pa.overallMargin}%
                                            </span>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            {/* Top Profitable + Low Margin */}
            <div
                className="dash-pa-grid"
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                }}
            >
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">
                            Top Profitable Products
                        </span>
                        <span
                            style={{ fontSize: 11, color: "var(--text-muted)" }}
                        >
                            Top 10
                        </span>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <ChartSkeleton height={180} />
                        ) : (
                            <TopProfitBars products={pa?.topProfitable ?? []} />
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <span className="card-title">Low Margin Products</span>
                        <span
                            style={{
                                fontSize: 11,
                                background: "#fee2e2",
                                color: "#dc2626",
                                padding: "2px 8px",
                                borderRadius: 4,
                                fontWeight: 600,
                            }}
                        >
                            Watch
                        </span>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {loading ? (
                            Array(4)
                                .fill(0)
                                .map((_, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            display: "flex",
                                            gap: 12,
                                            padding: "10px 14px",
                                            borderBottom: "1px solid #f9fafb",
                                        }}
                                    >
                                        <Sk h={12} w="40%" />
                                        <Sk h={12} w="20%" />
                                        <Sk h={12} w="18%" />
                                        <Sk h={12} w="16%" />
                                    </div>
                                ))
                        ) : !pa || pa.lowMargin.length === 0 ? (
                            <div
                                style={{
                                    padding: 20,
                                    textAlign: "center",
                                    color: "var(--text-muted)",
                                    fontSize: 13,
                                }}
                            >
                                No data for this period
                            </div>
                        ) : (
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: 12,
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            background: "#fef2f2",
                                            borderBottom: "1px solid #fecaca",
                                        }}
                                    >
                                        {[
                                            "Product",
                                            "Revenue",
                                            "Profit",
                                            "Margin",
                                        ].map((h) => (
                                            <th
                                                key={h}
                                                style={{
                                                    padding: "8px 14px",
                                                    textAlign:
                                                        h === "Product"
                                                            ? "left"
                                                            : "right",
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    color: "#b91c1c",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pa.lowMargin.map((p) => (
                                        <tr
                                            key={p.productId}
                                            style={{
                                                borderBottom:
                                                    "1px solid #f9fafb",
                                            }}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.background =
                                                    "#fff5f5")
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.background =
                                                    "")
                                            }
                                        >
                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    fontWeight: 600,
                                                    color: "#374151",
                                                    maxWidth: 150,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {p.productName}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                ₹
                                                {p.revenue.toLocaleString(
                                                    "en-IN",
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                    fontWeight: 700,
                                                    color:
                                                        p.profit >= 0
                                                            ? "#16a34a"
                                                            : "#dc2626",
                                                }}
                                            >
                                                {p.profit >= 0 ? "+" : ""}₹
                                                {p.profit.toLocaleString(
                                                    "en-IN",
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontWeight: 700,
                                                        color:
                                                            p.margin < 10
                                                                ? "#dc2626"
                                                                : "#d97706",
                                                        background:
                                                            p.margin < 10
                                                                ? "#fee2e2"
                                                                : "#fef3c7",
                                                        padding: "2px 7px",
                                                        borderRadius: 3,
                                                        fontSize: "0.72rem",
                                                    }}
                                                >
                                                    {p.margin.toFixed(1)}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Customer Profitability ────────────────────────────────────────────────────
function fmt(n) {
    return `₹${Number(n).toLocaleString("en-IN")}`;
}

function CustomerProfitability() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const toStr = (d) =>
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    // Returns date + 1 month (clamped to last day of that month)
    const addOneMonth = (dateStr) => {
        const d = new Date(dateStr);
        d.setMonth(d.getMonth() + 1);
        return toStr(d);
    };

    const defaultStart = toStr(new Date(now.getFullYear(), now.getMonth(), 1));
    const defaultEnd = addOneMonth(defaultStart);

    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [applied, setApplied] = useState({
        start: defaultStart,
        end: defaultEnd,
    });

    // Top-10 list (default view)
    const [top10, setTop10] = useState([]);
    const [total, setTotal] = useState(0);
    const [top10Loading, setTop10Loading] = useState(true);

    // Modal (server-side paginated)
    const [showModal, setShowModal] = useState(false);
    const [modalPage, setModalPage] = useState(1);
    const [modalData, setModalData] = useState([]);
    const [modalTotal, setModalTotal] = useState(0);
    const [modalLoading, setModalLoading] = useState(false);
    const MODAL_PER_PAGE = 50;

    const [expanded, setExpanded] = useState(new Set());

    // Fetch top 10 whenever applied range changes
    useEffect(() => {
        setTop10Loading(true);
        dashboardApi
            .getCustomerProfitability(applied.start, applied.end, 1, 10)
            .then((res) => {
                setTop10(res.data?.customers ?? []);
                setTotal(res.data?.total ?? 0);
            })
            .catch(() => setTop10([]))
            .finally(() => setTop10Loading(false));
    }, [applied]);

    // Fetch modal page whenever modal opens or page changes
    useEffect(() => {
        if (!showModal) return;
        setModalLoading(true);
        dashboardApi
            .getCustomerProfitability(
                applied.start,
                applied.end,
                modalPage,
                MODAL_PER_PAGE,
            )
            .then((res) => {
                setModalData(res.data?.customers ?? []);
                setModalTotal(res.data?.total ?? 0);
            })
            .catch(() => setModalData([]))
            .finally(() => setModalLoading(false));
    }, [showModal, modalPage, applied]);

    const openModal = () => {
        setModalPage(1);
        setModalData([]);
        setShowModal(true);
    };

    const toggleExpand = (id) =>
        setExpanded((prev) => {
            const s = new Set(prev);
            s.has(id) ? s.delete(id) : s.add(id);
            return s;
        });

    const maxProfit = top10[0]?.totalProfit || 1;
    const totalPages = Math.ceil(modalTotal / MODAL_PER_PAGE);

    const RANK_COLORS = ["#f59e0b", "#9ca3af", "#cd7c3e"];

    return (
        <div style={{ marginTop: 32 }}>
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 18,
                }}
            >
                <div
                    style={{
                        width: 4,
                        height: 22,
                        background: "var(--primary)",
                        borderRadius: 2,
                        flexShrink: 0,
                    }}
                />
                <span
                    style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "var(--text)",
                    }}
                >
                    Customer Profitability
                </span>
                <div
                    style={{ flex: 1, height: 1, background: "var(--border)" }}
                />
                {/* Date range */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexShrink: 0,
                    }}
                >
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setEndDate(addOneMonth(e.target.value));
                        }}
                        style={{
                            fontSize: 12,
                            border: "1px solid var(--border)",
                            borderRadius: 7,
                            padding: "5px 8px",
                            color: "var(--text)",
                            outline: "none",
                            cursor: "pointer",
                        }}
                    />
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        →
                    </span>
                    <input
                        type="date"
                        value={endDate}
                        min={startDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{
                            fontSize: 12,
                            border: "1px solid var(--border)",
                            borderRadius: 7,
                            padding: "5px 8px",
                            color: "var(--text)",
                            outline: "none",
                            cursor: "pointer",
                        }}
                    />
                    <button
                        onClick={() =>
                            setApplied({ start: startDate, end: endDate })
                        }
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "5px 14px",
                            borderRadius: 7,
                            cursor: "pointer",
                            border: "none",
                            background: "var(--primary)",
                            color: "#fff",
                        }}
                    >
                        Apply
                    </button>
                </div>
            </div>

            {/* Top 10 list */}
            <div className="card">
                <div className="card-header">
                    <span className="card-title">
                        Top 10 Customers by Profit
                    </span>
                    {!top10Loading && total > 0 && (
                        <button
                            onClick={openModal}
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                padding: "4px 14px",
                                borderRadius: 6,
                                cursor: "pointer",
                                border: "1.5px solid var(--primary)",
                                background: "#fff",
                                color: "var(--primary)",
                            }}
                        >
                            View All {total}
                        </button>
                    )}
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                    {top10Loading ? (
                        Array(5)
                            .fill(0)
                            .map((_, i) => (
                                <div
                                    key={i}
                                    style={{
                                        padding: "14px 20px",
                                        borderBottom: "1px solid #f1f5f9",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                    }}
                                >
                                    <div style={{ display: "flex", gap: 10 }}>
                                        <Sk h={14} w={24} radius={4} />
                                        <Sk h={14} w="40%" />
                                    </div>
                                    <Sk h={6} w="100%" radius={3} />
                                    <div style={{ display: "flex", gap: 16 }}>
                                        <Sk h={11} w="25%" />
                                        <Sk h={11} w="25%" />
                                        <Sk h={11} w="25%" />
                                    </div>
                                </div>
                            ))
                    ) : top10.length === 0 ? (
                        <div
                            style={{
                                padding: 28,
                                textAlign: "center",
                                color: "var(--text-muted)",
                                fontSize: 13,
                            }}
                        >
                            No profitable customers for this period
                        </div>
                    ) : (
                        top10.map((c, i) => (
                            <div
                                key={c.leadId}
                                style={{ borderBottom: "1px solid #f1f5f9" }}
                            >
                                {/* Customer row */}
                                <div
                                    style={{
                                        padding: "12px 20px",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                    }}
                                    onClick={() => toggleExpand(c.leadId)}
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.background =
                                            "#f8fafc")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.currentTarget.style.background = "")
                                    }
                                >
                                    {/* Rank */}
                                    <div
                                        style={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: 8,
                                            background:
                                                i < 3
                                                    ? RANK_COLORS[i]
                                                    : "#e5e7eb",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: i < 3 ? "#fff" : "#6b7280",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {i + 1}
                                    </div>
                                    {/* Name + orders */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 13,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {c.leadName}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: "var(--text-muted)",
                                                marginTop: 1,
                                            }}
                                        >
                                            {c.orderCount} order
                                            {c.orderCount !== 1 ? "s" : ""}
                                        </div>
                                    </div>
                                    {/* Profit bar */}
                                    <div style={{ width: 120, flexShrink: 0 }}>
                                        <div
                                            style={{
                                                height: 6,
                                                background: "#f3f4f6",
                                                borderRadius: 3,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    height: "100%",
                                                    width: `${(c.totalProfit / maxProfit) * 100}%`,
                                                    background: "#16a34a",
                                                    borderRadius: 3,
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {/* Profit value */}
                                    <div
                                        style={{
                                            textAlign: "right",
                                            minWidth: 90,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 800,
                                                fontSize: 14,
                                                color: "#16a34a",
                                            }}
                                        >
                                            {fmt(c.totalProfit)}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 10,
                                                color: "var(--text-muted)",
                                            }}
                                        >
                                            {c.margin}% margin
                                        </div>
                                    </div>
                                    {/* Revenue / Cost */}
                                    <div
                                        style={{
                                            textAlign: "right",
                                            minWidth: 80,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: "#3b82f6",
                                            }}
                                        >
                                            Rev: {fmt(c.totalRevenue)}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: "#f59e0b",
                                            }}
                                        >
                                            Cost: {fmt(c.totalCost)}
                                        </div>
                                    </div>
                                    {/* Expand icon */}
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted)",
                                            flexShrink: 0,
                                            transform: expanded.has(c.leadId)
                                                ? "rotate(180deg)"
                                                : "none",
                                            transition: "transform 0.2s",
                                        }}
                                    >
                                        ▼
                                    </div>
                                </div>
                                {/* Expanded products */}
                                {expanded.has(c.leadId) && (
                                    <div
                                        style={{
                                            background: "#f8fafc",
                                            borderTop: "1px solid #f1f5f9",
                                            padding: "0 20px 12px 60px",
                                        }}
                                    >
                                        <table
                                            style={{
                                                width: "100%",
                                                borderCollapse: "collapse",
                                                fontSize: 12,
                                            }}
                                        >
                                            <thead>
                                                <tr
                                                    style={{
                                                        color: "#9ca3af",
                                                        fontSize: "0.68rem",
                                                        textTransform:
                                                            "uppercase",
                                                        letterSpacing: "0.04em",
                                                    }}
                                                >
                                                    {[
                                                        "Product",
                                                        "Qty",
                                                        "Revenue",
                                                        "Cost",
                                                        "Profit",
                                                    ].map((h) => (
                                                        <th
                                                            key={h}
                                                            style={{
                                                                padding:
                                                                    "8px 10px 4px",
                                                                textAlign:
                                                                    h ===
                                                                    "Product"
                                                                        ? "left"
                                                                        : "right",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {h}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {c.products.map((p) => (
                                                    <tr key={p.productId}>
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "6px 10px",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {p.productName}
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "6px 10px",
                                                                textAlign:
                                                                    "right",
                                                            }}
                                                        >
                                                            {p.qty}
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "6px 10px",
                                                                textAlign:
                                                                    "right",
                                                                color: "#3b82f6",
                                                            }}
                                                        >
                                                            {fmt(p.revenue)}
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "6px 10px",
                                                                textAlign:
                                                                    "right",
                                                                color: "#f59e0b",
                                                            }}
                                                        >
                                                            {fmt(p.cost)}
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "6px 10px",
                                                                textAlign:
                                                                    "right",
                                                                fontWeight: 700,
                                                                color:
                                                                    p.profit >=
                                                                    0
                                                                        ? "#16a34a"
                                                                        : "#dc2626",
                                                            }}
                                                        >
                                                            {p.profit >= 0
                                                                ? "+"
                                                                : ""}
                                                            {fmt(p.profit)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Modal — all customers paginated */}
            {showModal && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.45)",
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 24,
                    }}
                    onClick={(e) =>
                        e.target === e.currentTarget && setShowModal(false)
                    }
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: 16,
                            width: "100%",
                            maxWidth: 900,
                            maxHeight: "88vh",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
                        }}
                    >
                        {/* Modal header */}
                        <div
                            style={{
                                padding: "18px 24px",
                                borderBottom: "1px solid var(--border)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexShrink: 0,
                            }}
                        >
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 15 }}>
                                    All Customers by Profit
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "var(--text-muted)",
                                        marginTop: 2,
                                    }}
                                >
                                    {applied.start} → {applied.end} · {total}{" "}
                                    profitable customers
                                </div>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    border: "1px solid var(--border)",
                                    background: "#fff",
                                    cursor: "pointer",
                                    fontSize: 16,
                                    color: "var(--text-muted)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                ×
                            </button>
                        </div>
                        {/* Modal body */}
                        <div style={{ overflowY: "auto", flex: 1 }}>
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: 13,
                                }}
                            >
                                <thead
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        background: "#f8fafc",
                                        zIndex: 1,
                                    }}
                                >
                                    <tr
                                        style={{
                                            borderBottom:
                                                "2px solid var(--border)",
                                        }}
                                    >
                                        {[
                                            "#",
                                            "Customer",
                                            "Orders",
                                            "Revenue",
                                            "Cost",
                                            "Profit",
                                            "Margin",
                                            "",
                                        ].map((h) => (
                                            <th
                                                key={h}
                                                style={{
                                                    padding: "10px 16px",
                                                    textAlign: [
                                                        "#",
                                                        "Orders",
                                                        "Margin",
                                                        "",
                                                    ].includes(h)
                                                        ? "center"
                                                        : h === "Customer"
                                                          ? "left"
                                                          : "right",
                                                    fontWeight: 600,
                                                    fontSize: "0.72rem",
                                                    color: "#6b7280",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalLoading
                                        ? Array(8)
                                              .fill(0)
                                              .map((_, i) => (
                                                  <tr key={i}>
                                                      <td
                                                          colSpan={8}
                                                          style={{
                                                              padding:
                                                                  "12px 16px",
                                                          }}
                                                      >
                                                          <Sk h={12} />
                                                      </td>
                                                  </tr>
                                              ))
                                        : modalData.map((c, i) => {
                                              const rank =
                                                  (modalPage - 1) *
                                                      MODAL_PER_PAGE +
                                                  i +
                                                  1;
                                              const isExp = expanded.has(
                                                  c.leadId,
                                              );
                                              return [
                                                  <tr
                                                      key={c.leadId}
                                                      style={{
                                                          borderBottom: isExp
                                                              ? "none"
                                                              : "1px solid #f1f5f9",
                                                          cursor: "pointer",
                                                      }}
                                                      onClick={() =>
                                                          toggleExpand(c.leadId)
                                                      }
                                                      onMouseEnter={(e) =>
                                                          (e.currentTarget.style.background =
                                                              "#f8fafc")
                                                      }
                                                      onMouseLeave={(e) =>
                                                          (e.currentTarget.style.background =
                                                              "")
                                                      }
                                                  >
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              textAlign:
                                                                  "center",
                                                              color: "var(--text-muted)",
                                                              fontSize: 12,
                                                          }}
                                                      >
                                                          {rank}
                                                      </td>
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              fontWeight: 700,
                                                          }}
                                                      >
                                                          {c.leadName}
                                                      </td>
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              textAlign:
                                                                  "center",
                                                          }}
                                                      >
                                                          {c.orderCount}
                                                      </td>
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              textAlign:
                                                                  "right",
                                                              color: "#3b82f6",
                                                          }}
                                                      >
                                                          {fmt(c.totalRevenue)}
                                                      </td>
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              textAlign:
                                                                  "right",
                                                              color: "#f59e0b",
                                                          }}
                                                      >
                                                          {fmt(c.totalCost)}
                                                      </td>
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              textAlign:
                                                                  "right",
                                                              fontWeight: 800,
                                                              color: "#16a34a",
                                                          }}
                                                      >
                                                          {fmt(c.totalProfit)}
                                                      </td>
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              textAlign:
                                                                  "center",
                                                          }}
                                                      >
                                                          <span
                                                              style={{
                                                                  fontSize:
                                                                      "0.73rem",
                                                                  fontWeight: 700,
                                                                  padding:
                                                                      "2px 8px",
                                                                  borderRadius: 4,
                                                                  background:
                                                                      c.margin >=
                                                                      30
                                                                          ? "#dcfce7"
                                                                          : "#fef3c7",
                                                                  color:
                                                                      c.margin >=
                                                                      30
                                                                          ? "#16a34a"
                                                                          : "#d97706",
                                                              }}
                                                          >
                                                              {c.margin}%
                                                          </span>
                                                      </td>
                                                      <td
                                                          style={{
                                                              padding:
                                                                  "10px 16px",
                                                              textAlign:
                                                                  "center",
                                                              fontSize: 11,
                                                              color: "var(--text-muted)",
                                                              transform: isExp
                                                                  ? "rotate(180deg)"
                                                                  : "none",
                                                              display:
                                                                  "inline-block",
                                                              transition:
                                                                  "transform 0.2s",
                                                          }}
                                                      >
                                                          ▼
                                                      </td>
                                                  </tr>,
                                                  isExp && (
                                                      <tr
                                                          key={`${c.leadId}-exp`}
                                                          style={{
                                                              background:
                                                                  "#f8fafc",
                                                              borderBottom:
                                                                  "1px solid #f1f5f9",
                                                          }}
                                                      >
                                                          <td
                                                              colSpan={8}
                                                              style={{
                                                                  padding:
                                                                      "0 16px 12px 50px",
                                                              }}
                                                          >
                                                              <table
                                                                  style={{
                                                                      width: "100%",
                                                                      borderCollapse:
                                                                          "collapse",
                                                                      fontSize: 12,
                                                                  }}
                                                              >
                                                                  <thead>
                                                                      <tr
                                                                          style={{
                                                                              color: "#9ca3af",
                                                                              fontSize:
                                                                                  "0.67rem",
                                                                              textTransform:
                                                                                  "uppercase",
                                                                          }}
                                                                      >
                                                                          {[
                                                                              "Product",
                                                                              "Qty",
                                                                              "Revenue",
                                                                              "Cost",
                                                                              "Profit",
                                                                          ].map(
                                                                              (
                                                                                  h,
                                                                              ) => (
                                                                                  <th
                                                                                      key={
                                                                                          h
                                                                                      }
                                                                                      style={{
                                                                                          padding:
                                                                                              "6px 10px 3px",
                                                                                          textAlign:
                                                                                              h ===
                                                                                              "Product"
                                                                                                  ? "left"
                                                                                                  : "right",
                                                                                          fontWeight: 600,
                                                                                      }}
                                                                                  >
                                                                                      {
                                                                                          h
                                                                                      }
                                                                                  </th>
                                                                              ),
                                                                          )}
                                                                      </tr>
                                                                  </thead>
                                                                  <tbody>
                                                                      {c.products.map(
                                                                          (
                                                                              p,
                                                                          ) => (
                                                                              <tr
                                                                                  key={
                                                                                      p.productId
                                                                                  }
                                                                              >
                                                                                  <td
                                                                                      style={{
                                                                                          padding:
                                                                                              "5px 10px",
                                                                                          fontWeight: 600,
                                                                                      }}
                                                                                  >
                                                                                      {
                                                                                          p.productName
                                                                                      }
                                                                                  </td>
                                                                                  <td
                                                                                      style={{
                                                                                          padding:
                                                                                              "5px 10px",
                                                                                          textAlign:
                                                                                              "right",
                                                                                      }}
                                                                                  >
                                                                                      {
                                                                                          p.qty
                                                                                      }
                                                                                  </td>
                                                                                  <td
                                                                                      style={{
                                                                                          padding:
                                                                                              "5px 10px",
                                                                                          textAlign:
                                                                                              "right",
                                                                                          color: "#3b82f6",
                                                                                      }}
                                                                                  >
                                                                                      {fmt(
                                                                                          p.revenue,
                                                                                      )}
                                                                                  </td>
                                                                                  <td
                                                                                      style={{
                                                                                          padding:
                                                                                              "5px 10px",
                                                                                          textAlign:
                                                                                              "right",
                                                                                          color: "#f59e0b",
                                                                                      }}
                                                                                  >
                                                                                      {fmt(
                                                                                          p.cost,
                                                                                      )}
                                                                                  </td>
                                                                                  <td
                                                                                      style={{
                                                                                          padding:
                                                                                              "5px 10px",
                                                                                          textAlign:
                                                                                              "right",
                                                                                          fontWeight: 700,
                                                                                          color:
                                                                                              p.profit >=
                                                                                              0
                                                                                                  ? "#16a34a"
                                                                                                  : "#dc2626",
                                                                                      }}
                                                                                  >
                                                                                      {p.profit >=
                                                                                      0
                                                                                          ? "+"
                                                                                          : ""}
                                                                                      {fmt(
                                                                                          p.profit,
                                                                                      )}
                                                                                  </td>
                                                                              </tr>
                                                                          ),
                                                                      )}
                                                                  </tbody>
                                                              </table>
                                                          </td>
                                                      </tr>
                                                  ),
                                              ];
                                          })}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div
                                style={{
                                    padding: "12px 24px",
                                    borderTop: "1px solid var(--border)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    flexShrink: 0,
                                }}
                            >
                                <button
                                    onClick={() =>
                                        setModalPage((p) => Math.max(1, p - 1))
                                    }
                                    disabled={modalPage === 1}
                                    style={{
                                        padding: "5px 12px",
                                        borderRadius: 6,
                                        border: "1px solid var(--border)",
                                        background: "#fff",
                                        cursor:
                                            modalPage === 1
                                                ? "not-allowed"
                                                : "pointer",
                                        color:
                                            modalPage === 1
                                                ? "#d1d5db"
                                                : "var(--text)",
                                        fontSize: 13,
                                        fontWeight: 600,
                                    }}
                                >
                                    ←
                                </button>
                                {Array.from(
                                    { length: totalPages },
                                    (_, i) => i + 1,
                                )
                                    .filter(
                                        (p) =>
                                            p === 1 ||
                                            p === totalPages ||
                                            Math.abs(p - modalPage) <= 2,
                                    )
                                    .reduce((acc, p, i, arr) => {
                                        if (i > 0 && p - arr[i - 1] > 1)
                                            acc.push("…");
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, i) =>
                                        p === "…" ? (
                                            <span
                                                key={`e${i}`}
                                                style={{
                                                    padding: "0 4px",
                                                    color: "var(--text-muted)",
                                                    fontSize: 13,
                                                }}
                                            >
                                                …
                                            </span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => setModalPage(p)}
                                                style={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: 6,
                                                    border: "1px solid",
                                                    borderColor:
                                                        p === modalPage
                                                            ? "var(--primary)"
                                                            : "var(--border)",
                                                    background:
                                                        p === modalPage
                                                            ? "var(--primary)"
                                                            : "#fff",
                                                    color:
                                                        p === modalPage
                                                            ? "#fff"
                                                            : "var(--text)",
                                                    cursor: "pointer",
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {p}
                                            </button>
                                        ),
                                    )}
                                <button
                                    onClick={() =>
                                        setModalPage((p) =>
                                            Math.min(totalPages, p + 1),
                                        )
                                    }
                                    disabled={modalPage === totalPages}
                                    style={{
                                        padding: "5px 12px",
                                        borderRadius: 6,
                                        border: "1px solid var(--border)",
                                        background: "#fff",
                                        cursor:
                                            modalPage === totalPages
                                                ? "not-allowed"
                                                : "pointer",
                                        color:
                                            modalPage === totalPages
                                                ? "#d1d5db"
                                                : "var(--text)",
                                        fontSize: 13,
                                        fontWeight: 600,
                                    }}
                                >
                                    →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main AdminDashboard ───────────────────────────────────────────────────────
export default function AdminDashboard({ onNavigate }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const now = new Date();
    const defaultRangeStart = toDateStr(
        new Date(now.getFullYear(), now.getMonth(), 1),
    );
    const defaultRangeEnd = toDateStr(now);

    const storedRange = loadStoredRange(defaultRangeStart, defaultRangeEnd);
    const [rangeStart, setRangeStart] = useState(storedRange.start);
    const [rangeEnd, setRangeEnd] = useState(storedRange.end);
    const [appliedRange, setAppliedRange] = useState(storedRange);

    const [breakdownModal, setBreakdownModal] = useState(null); // "status" | "delivery" | null

    useEffect(() => {
        setLoading(true);
        dashboardApi
            .getAdminDashboard({
                startDate: appliedRange.start,
                endDate: appliedRange.end,
            })
            .then((res) => setData(res.data ?? null))
            .catch((err) => console.error("Dashboard load error:", err))
            .finally(() => setLoading(false));
    }, [appliedRange]);

    const s = data?.summary;

    // Bar chart: ordersPerDay → [{label, value}]
    const barData = (data?.ordersPerDay ?? []).map((d) => ({
        label: new Date(d.date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
        }),
        value: d.count,
    }));

    const totalOrders = s?.totalOrders ?? 0;
    const rangeStartApplied = appliedRange.start;
    const rangeEndApplied = appliedRange.end;

    // Full 8-status breakdown, shared by the status-card grid, the Order Status legend, and the donut.
    const statusMap = Object.fromEntries(
        (data?.ordersByStatus ?? []).map((r) => [r.status, r.count]),
    );
    const statusSegments = STATUS_ORDER.map((key) => ({
        status: key,
        label: STATUS_CONFIG[key].label,
        color: STATUS_CONFIG[key].color,
        count: statusMap[key] ?? 0,
    }));
    // Note: "Delivered" is dispatch-date based while every other status here is created-date
    // based (see statusOrderLink below), so this sum is a display total for the donut/legend —
    // it won't necessarily match the "Total Orders" stat card above, which is purely dispatch-date based.
    const statusSegmentsTotal = statusSegments.reduce(
        (sum, s) => sum + s.count,
        0,
    );

    // Delivery mode breakdown → donut segments, cycling through a fixed palette.
    const DELIVERY_MODE_COLORS = [
        "#3b82f6",
        "#8b5cf6",
        "#f59e0b",
        "#16a34a",
        "#06b6d4",
        "#ec4899",
        "#6b7280",
    ];
    const deliveryModeSegments = [...(data?.deliveryModeBreakdown ?? [])]
        .sort((a, b) => b.count - a.count)
        .map((r, i) => ({
            label: r.mode,
            count: r.count,
            color: DELIVERY_MODE_COLORS[i % DELIVERY_MODE_COLORS.length],
        }));
    const deliveryModeTotal = deliveryModeSegments.reduce(
        (sum, r) => sum + r.count,
        0,
    );

    // Product category breakdown → donut segments, same palette convention as delivery mode.
    const productCategorySegments = [
        ...(data?.productCategoryBreakdown ?? []),
    ]
        .sort((a, b) => b.count - a.count)
        .map((r, i) => ({
            label: r.category,
            count: r.count,
            color: DELIVERY_MODE_COLORS[i % DELIVERY_MODE_COLORS.length],
        }));
    const productCategoryTotal = productCategorySegments.reduce(
        (sum, r) => sum + r.count,
        0,
    );

    // "Delivered" is counted by dispatch date (matches Total Orders / Profitability); every
    // other status is counted by created date — so the Orders-page deep link must match per status.
    const statusOrderLink = (status) =>
        status === "delivered"
            ? `/orders?status=delivered&date_from=${rangeStartApplied}&date_to=${rangeEndApplied}`
            : `/orders?status=${status}&created_from=${rangeStartApplied}&created_to=${rangeEndApplied}`;

    return (
        <div className="page-body">
            {/* ── Page header ── */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 20,
                    flexWrap: "wrap",
                    gap: 12,
                }}
            >
                <div>
                    <div
                        style={{
                            fontSize: 20,
                            fontWeight: 800,
                            color: "var(--text)",
                        }}
                    >
                        Admin Dashboard
                    </div>
                    <div
                        style={{
                            fontSize: 12,
                            color: "var(--text-muted)",
                            marginTop: 2,
                        }}
                    >
                        {fmtRangeDate(appliedRange.start)} –{" "}
                        {fmtRangeDate(appliedRange.end)}
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                    }}
                >
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/place-order")}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                        }}
                    >
                        <PlusCircle size={15} strokeWidth={2} /> Place New Order
                    </button>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        <input
                            type="date"
                            value={rangeStart}
                            max={rangeEnd}
                            onChange={(e) => {
                                setRangeStart(e.target.value);
                                if (rangeEnd < e.target.value)
                                    setRangeEnd(e.target.value);
                            }}
                            style={PROFIT_DATE_INPUT_STYLE}
                        />
                        <span
                            style={{ fontSize: 12, color: "var(--text-muted)" }}
                        >
                            →
                        </span>
                        <input
                            type="date"
                            value={rangeEnd}
                            min={rangeStart}
                            onChange={(e) => setRangeEnd(e.target.value)}
                            style={PROFIT_DATE_INPUT_STYLE}
                        />
                        <button
                            onClick={() => {
                                const next = {
                                    start: rangeStart,
                                    end: rangeEnd,
                                };
                                setAppliedRange(next);
                                saveStoredRange(next);
                            }}
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                padding: "7px 14px",
                                borderRadius: 7,
                                cursor: "pointer",
                                border: "none",
                                background: "var(--primary)",
                                color: "#fff",
                            }}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Overview KPIs ── */}
            {loading ? (
                <>
                    <div
                        className="stats-grid dash-sum-grid"
                        style={{
                            gridTemplateColumns: "repeat(3, 1fr)",
                            marginBottom: 10,
                        }}
                    >
                        {Array(3)
                            .fill(0)
                            .map((_, i) => (
                                <div
                                    key={i}
                                    className="stat-card"
                                    style={{ gap: 10 }}
                                >
                                    <Sk h={12} w="60%" />
                                    <Sk
                                        h={28}
                                        w="50%"
                                        style={{ marginTop: 4 }}
                                    />
                                    <Sk
                                        h={10}
                                        w="70%"
                                        style={{ marginTop: 4 }}
                                    />
                                </div>
                            ))}
                    </div>
                    <div
                        className="stats-grid"
                        style={{
                            gridTemplateColumns:
                                "repeat(auto-fill,minmax(155px,1fr))",
                            marginBottom: 20,
                        }}
                    >
                        {Array(8)
                            .fill(0)
                            .map((_, i) => (
                                <div
                                    key={i}
                                    className="stat-card"
                                    style={{ gap: 6, padding: "14px 16px" }}
                                >
                                    <Sk h={11} w="65%" />
                                    <Sk
                                        h={26}
                                        w="40%"
                                        style={{ marginTop: 6 }}
                                    />
                                    <Sk
                                        h={10}
                                        w="55%"
                                        style={{ marginTop: 4 }}
                                    />
                                </div>
                            ))}
                    </div>
                </>
            ) : (
                <>
                    {/* Row 1: Summary stats */}
                    <div
                        className="stats-grid dash-sum-grid"
                        style={{
                            gridTemplateColumns: "repeat(3, 1fr)",
                            marginBottom: 10,
                        }}
                    >
                        {[
                            {
                                label: "New Customers",
                                value: s?.totalCustomers ?? 0,
                                sub: "Selected range",
                                Icon: Users,
                                iconColor: "#6b7280",
                                iconBg: "#f3f4f6",
                                color: "",
                                to: `/customers?created_from=${rangeStartApplied}&created_to=${rangeEndApplied}`,
                            },
                            {
                                label: "Total Orders",
                                value: totalOrders,
                                sub: "Selected range",
                                Icon: Package,
                                iconColor: "#7c3aed",
                                iconBg: "#f5f3ff",
                                color: "purple",
                                to: `/orders?date_from=${rangeStartApplied}&date_to=${rangeEndApplied}`,
                            },
                            {
                                label: "Total Revenue",
                                value: fmt(s?.totalRevenue ?? 0),
                                sub: "Selected range · Delivered",
                                Icon: Banknote,
                                iconColor: "#16a34a",
                                iconBg: "#f0fdf4",
                                color: "green",
                                to: `/orders?status=delivered&date_from=${rangeStartApplied}&date_to=${rangeEndApplied}`,
                            },
                        ].map(
                            ({
                                label,
                                value,
                                sub,
                                Icon,
                                iconColor,
                                iconBg,
                                color,
                                to,
                            }) => (
                                <div
                                    key={label}
                                    className="stat-card"
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                        cursor: "pointer",
                                    }}
                                    onClick={() => navigate(to)}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <div
                                            className="label"
                                            style={{ fontSize: 11 }}
                                        >
                                            {label}
                                        </div>
                                        <div
                                            style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 8,
                                                background: iconBg,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Icon
                                                size={16}
                                                color={iconColor}
                                                strokeWidth={2}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className={`value ${color}`}
                                        style={{
                                            fontSize: 28,
                                            fontWeight: 800,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {value}
                                    </div>
                                    <div
                                        className="change up"
                                        style={{ fontSize: 11 }}
                                    >
                                        {sub}
                                    </div>
                                </div>
                            ),
                        )}
                    </div>
                    {/* Row 2: All status counts */}
                    {(() => {
                        return (
                            <div
                                className="stats-grid"
                                style={{
                                    gridTemplateColumns:
                                        "repeat(auto-fill,minmax(155px,1fr))",
                                    marginBottom: 20,
                                }}
                            >
                                {STATUS_ORDER.map((key) => {
                                    const cfg = STATUS_CONFIG[key];
                                    const count = statusMap[key] ?? 0;
                                    const { Icon } = cfg;
                                    return (
                                        <div
                                            key={key}
                                            className="stat-card"
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 6,
                                                borderTop: `3px solid ${cfg.color}`,
                                                padding: "14px 16px",
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                navigate(statusOrderLink(key))
                                            }
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "space-between",
                                                }}
                                            >
                                                <div
                                                    className="label"
                                                    style={{ fontSize: 11 }}
                                                >
                                                    {cfg.label}
                                                </div>
                                                <div
                                                    style={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: 8,
                                                        background: cfg.bg,
                                                        border: `1px solid ${cfg.border}`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <Icon
                                                        size={14}
                                                        color={cfg.color}
                                                        strokeWidth={2}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 28,
                                                    fontWeight: 800,
                                                    lineHeight: 1,
                                                    color:
                                                        count > 0
                                                            ? cfg.color
                                                            : "#d1d5db",
                                                }}
                                            >
                                                {count}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color: "var(--text-muted)",
                                                }}
                                            >
                                                Selected range
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </>
            )}

            {/* ── Orders section ── */}
            <SectionDivider title="Orders Overview" />

            {/* Revenue trend — full width */}
            <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header">
                    <span className="card-title">
                        Revenue Trend (Last 12 Months)
                    </span>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        Delivered orders · by dispatch date
                    </span>
                </div>
                <div className="card-body">
                    {loading ? (
                        <ChartSkeleton height={280} />
                    ) : (
                        <RevenueTrendChart data={data?.revenueByMonth ?? []} />
                    )}
                </div>
            </div>

            <div
                className="dash-2col"
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 16,
                    marginBottom: 16,
                    alignItems: "start",
                }}
            >
                {/* Order status */}
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">Order Status</span>
                    </div>
                    <div
                        className="card-body"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 16,
                            padding: "18px 16px",
                        }}
                    >
                        {loading ? (
                            <Sk h={190} w={190} radius={95} />
                        ) : (
                            <>
                                <div style={{ position: "relative" }}>
                                    <DonutChart
                                        segments={statusSegments}
                                        size={190}
                                    />
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%,-50%)",
                                            textAlign: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 800,
                                                fontSize: 24,
                                            }}
                                        >
                                            {statusSegmentsTotal}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: "var(--text-muted)",
                                            }}
                                        >
                                            Total
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setBreakdownModal("status")}
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        padding: "6px 16px",
                                        borderRadius: 20,
                                        cursor: "pointer",
                                        border: "1.5px solid var(--primary)",
                                        background: "#fff",
                                        color: "var(--primary)",
                                    }}
                                >
                                    View Breakdown
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Delivery mode */}
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">Delivery Mode</span>
                        <span
                            style={{ fontSize: 11, color: "var(--text-muted)" }}
                        >
                            Delivered orders · by dispatch date
                        </span>
                    </div>
                    <div
                        className="card-body"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 16,
                            padding: "18px 16px",
                        }}
                    >
                        {loading ? (
                            <Sk h={190} w={190} radius={95} />
                        ) : deliveryModeSegments.length === 0 ? (
                            <div
                                style={{
                                    fontSize: 12,
                                    color: "var(--text-muted)",
                                    padding: "40px 0",
                                }}
                            >
                                No orders in this range
                            </div>
                        ) : (
                            <>
                                <div style={{ position: "relative" }}>
                                    <DonutChart
                                        segments={deliveryModeSegments}
                                        size={190}
                                    />
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%,-50%)",
                                            textAlign: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 800,
                                                fontSize: 24,
                                            }}
                                        >
                                            {deliveryModeTotal}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: "var(--text-muted)",
                                            }}
                                        >
                                            Total
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() =>
                                        setBreakdownModal("delivery")
                                    }
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        padding: "6px 16px",
                                        borderRadius: 20,
                                        cursor: "pointer",
                                        border: "1.5px solid var(--primary)",
                                        background: "#fff",
                                        color: "var(--primary)",
                                    }}
                                >
                                    View Breakdown
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Product category */}
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">Product Category</span>
                        <span
                            style={{ fontSize: 11, color: "var(--text-muted)" }}
                        >
                            Delivered · by dispatch date
                        </span>
                    </div>
                    <div
                        className="card-body"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 16,
                            padding: "18px 16px",
                        }}
                    >
                        {loading ? (
                            <Sk h={190} w={190} radius={95} />
                        ) : productCategorySegments.length === 0 ? (
                            <div
                                style={{
                                    fontSize: 12,
                                    color: "var(--text-muted)",
                                    padding: "40px 0",
                                }}
                            >
                                No orders in this range
                            </div>
                        ) : (
                            <>
                                <div style={{ position: "relative" }}>
                                    <DonutChart
                                        segments={productCategorySegments}
                                        size={190}
                                    />
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%,-50%)",
                                            textAlign: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 800,
                                                fontSize: 24,
                                            }}
                                        >
                                            {productCategoryTotal}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: "var(--text-muted)",
                                            }}
                                        >
                                            Total
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() =>
                                        setBreakdownModal("category")
                                    }
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        padding: "6px 16px",
                                        borderRadius: 20,
                                        cursor: "pointer",
                                        border: "1.5px solid var(--primary)",
                                        background: "#fff",
                                        color: "var(--primary)",
                                    }}
                                >
                                    View Breakdown
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {breakdownModal === "status" && (
                <BreakdownModal
                    title="Order Status Breakdown"
                    segments={statusSegments}
                    total={statusSegmentsTotal}
                    onClose={() => setBreakdownModal(null)}
                    onRowClick={(r) => navigate(statusOrderLink(r.status))}
                />
            )}
            {breakdownModal === "delivery" && (
                <BreakdownModal
                    title="Delivery Mode Breakdown"
                    segments={deliveryModeSegments}
                    total={deliveryModeTotal}
                    onClose={() => setBreakdownModal(null)}
                />
            )}
            {breakdownModal === "category" && (
                <BreakdownModal
                    title="Product Category Breakdown"
                    segments={productCategorySegments}
                    total={productCategoryTotal}
                    onClose={() => setBreakdownModal(null)}
                />
            )}

            {/* ── Profitability ── */}
            <ProfitabilitySection
                startDate={appliedRange.start}
                endDate={appliedRange.end}
            />

            {/* ── Product Analytics ── */}
            {/*<ProductAnalyticsDashboard
                onNavigate={onNavigate}
            />*/}

            {/* ── Customer Profitability ── */}
            {/*<CustomerProfitability /> */}
        </div>
    );
}
